import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Pencil, Save, X, Trash2, Copy, MessageCircle, Mail, Phone, Info, Users,
  FileStack, History, CheckCircle2, XCircle, Undo2, ShieldCheck, Paperclip, FileSignature,
  Download, ExternalLink, Building2, ChevronsUpDown, Check, Repeat, Plus, Send,
} from "lucide-react";
import IconAction from "@/components/app/IconAction";
import api, { fmtIDR, fmtDate } from "@/lib_app/api";
import { toast } from "sonner";
import Avatar from "@/components/app/Avatar";
import NumberInput from "@/components/app/NumberInput";
import DateInput from "@/components/app/DateInput";
import { useAuth } from "@/context/AuthContext";
import { LEAD_SOURCE_META } from "@/components/app/ProspectFormModal";
import QuotationAttachments from "@/components/app/QuotationAttachments";

const RATE_META = {
  1: { label: "Hot", chip: "bg-[#FDE7E9] text-[#CE6968] border-[#FFB3BF]", heroBg: "linear-gradient(135deg, rgba(255,193,203,0.55), rgba(255,222,228,0.35))", heroBorder: "rgba(255,140,155,0.45)", winProb: 80 },
  2: { label: "Warm", chip: "bg-[#FFDB52] text-[#7A5B00] border-[#F0D28F]", heroBg: "linear-gradient(135deg, rgba(255,232,178,0.65), rgba(255,244,214,0.35))", heroBorder: "rgba(240,200,110,0.45)", winProb: 55 },
  3: { label: "Cold", chip: "bg-[#E5EEFA] text-[#3B4A63] border-[#B0C7EA]", heroBg: "linear-gradient(135deg, rgba(190,210,240,0.55), rgba(232,240,254,0.35))", heroBorder: "rgba(150,175,220,0.45)", winProb: 25 },
};
const STATUS_META = {
  won: { label: "Deal / Won", chip: "bg-[#57D28A] text-white", heroBg: "linear-gradient(135deg, rgba(180,240,205,0.55), rgba(215,247,225,0.35))", heroBorder: "rgba(120,200,150,0.45)" },
  lost: { label: "Lost", chip: "bg-[#A0A3A8] text-white", heroBg: "linear-gradient(135deg, rgba(220,224,231,0.55), rgba(240,242,246,0.35))", heroBorder: "rgba(160,163,168,0.45)" },
};
const CATEGORY_LABEL = { pemerintah: "Pemerintah", bumn: "BUMN", swasta: "Swasta", partner: "Partner" };

function computeAgeDays(iso) {
  if (!iso) return 0;
  try { return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)); } catch { return 0; }
}
function cleanPhone(raw) {
  let x = (raw || "").replace(/[^0-9+]/g, "");
  if (x.startsWith("0")) x = "62" + x.slice(1);
  else if (x.startsWith("+")) x = x.slice(1);
  else if (x.startsWith("8")) x = "62" + x;
  return x;
}

export default function ProspectDetailModal({ open, prospect: initial, users = [], onClose, onChanged, onOpenEditForm, onDeal, onLost, onReopen, onDelete }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState(initial);
  const [mode, setMode] = useState("read");
  const [tab, setTab] = useState("info");
  const [form, setForm] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [clients, setClients] = useState([]);
  const attachInputRef = useRef(null);

  const userMap = useMemo(() => {
    const m = {};
    (users || []).forEach((u) => { m[u.user_id] = u; });
    return m;
  }, [users]);

  const isOwner = user?.role === "owner";
  const isDirector = user?.role === "sales_director";
  const canEdit = isOwner || isDirector || (user && prospect?.owner_id === user.user_id);
  const canDelete = isOwner || isDirector;

  useEffect(() => { setProspect(initial); }, [initial]);
  useEffect(() => {
    if (!open || !prospect) return;
    setForm({
      customer: prospect.customer || "",
      client_id: prospect.client_id || "",
      client_category: prospect.client_category || "",
      service: prospect.service || "",
      rate: prospect.rate || 2,
      prospect_value: prospect.prospect_value || 0,
      realization: prospect.realization || 0,
      lead_source: prospect.lead_source || "",
      lead_source_detail: prospect.lead_source_detail || "",
      pipeline_month: prospect.pipeline_month || "",
      last_follow_up: prospect.last_follow_up || "",
      remark: prospect.remark || "",
    });
    setMode("read");
    setTab("info");
    refreshHistory();
    setAttachments(prospect.attachments?.filter((a) => !a.is_deleted) || []);
    // Load clients list for the "Ganti Klien" picker
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, [open, prospect?.id]);

  if (!prospect) return null;

  const refreshHistory = async () => {
    if (!prospect?.id) return;
    try { const r = await api.get(`/prospects/${prospect.id}/history`); setHistory(r.data || []); } catch { setHistory([]); }
  };
  const refreshProspect = async () => {
    if (!prospect?.id) return;
    try {
      const r = await api.get(`/prospects/${prospect.id}`);
      setProspect(r.data);
      setAttachments(r.data.attachments?.filter((a) => !a.is_deleted) || []);
    } catch {}
  };

  const status = prospect.status || "active";
  const isWon = status === "won";
  const isLost = status === "lost";
  const meta = isWon ? STATUS_META.won : isLost ? STATUS_META.lost : RATE_META[prospect.rate] || RATE_META[2];
  const ageDays = computeAgeDays(prospect.created_at);
  const winProb = (RATE_META[prospect.rate] || {}).winProb || 0;
  const source = prospect.lead_source ? LEAD_SOURCE_META[prospect.lead_source] : null;

  const saveDetail = async () => {
    setSaving(true);
    try {
      const payload = {
        customer: form.customer, service: form.service, rate: Number(form.rate),
        prospect_value: Number(form.prospect_value) || 0,
        realization: Number(form.realization) || 0,
        // Preserve PIC fields from current prospect (may have been updated via applyPic in PIC tab)
        pic_id: prospect.pic_id || "",
        pic_name: prospect.pic_name || "",
        pic_position: prospect.pic_position || "",
        pic_phone: prospect.pic_phone || "",
        pic_email: prospect.pic_email || "",
        lead_source: form.lead_source || null,
        lead_source_detail: form.lead_source_detail || "",
        pipeline_month: form.pipeline_month || null,
        last_follow_up: form.last_follow_up || null,
        remark: form.remark || "",
      };
      // Send client_id only if user explicitly changed it via picker — backend then re-links & syncs customer name
      if (form.client_id && form.client_id !== prospect.client_id) {
        payload.client_id = form.client_id;
      }
      await api.patch(`/prospects/${prospect.id}`, payload);
      toast.success("Berhasil disimpan");
      await refreshProspect();
      await refreshHistory();
      onChanged?.();
      setMode("read");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };

  const cancelEdit = () => {    setForm({
      customer: prospect.customer || "", service: prospect.service || "",
      client_id: prospect.client_id || "",
      client_category: prospect.client_category || "",
      rate: prospect.rate || 2,
      prospect_value: prospect.prospect_value || 0,
      realization: prospect.realization || 0,
      lead_source: prospect.lead_source || "",
      lead_source_detail: prospect.lead_source_detail || "",
      pipeline_month: prospect.pipeline_month || "",
      last_follow_up: prospect.last_follow_up || "",
      remark: prospect.remark || "",
    });
    setMode("read");
  };

  const quickFollowUp = async () => {
    const today = new Date().toISOString().slice(0, 10);
    setSaving(true);
    try {
      await api.patch(`/prospects/${prospect.id}`, {
        customer: prospect.customer,
        service: prospect.service,
        rate: prospect.rate,
        prospect_value: prospect.prospect_value || 0,
        realization: prospect.realization || 0,
        pic_id: prospect.pic_id || "",
        pic_name: prospect.pic_name || "",
        pic_position: prospect.pic_position || "",
        pic_phone: prospect.pic_phone || "",
        pic_email: prospect.pic_email || "",
        last_follow_up: today,
        remark: prospect.remark || "",
        pipeline_month: prospect.pipeline_month || null,
        lead_source: prospect.lead_source || null,
        lead_source_detail: prospect.lead_source_detail || "",
      });
      toast.success("Follow-up dicatat hari ini");
      await refreshProspect();
      await refreshHistory();
      onChanged?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Gagal update follow-up");
    }
    finally { setSaving(false); }
  };

  const uploadAttachments = async (files) => {
    if (!files || files.length === 0) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      await api.post(`/prospects/${prospect.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${files.length} lampiran diupload`);
      await refreshProspect();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal upload"); }
    finally { setUploading(false); }
  };

  const openAttachment = (attId) => {
    const token = localStorage.getItem("sp_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/prospects/${prospect.id}/attachments/${attId}/download?auth=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank");
  };

  const deleteAttachment = async (att, e) => {
    e?.stopPropagation();
    if (!confirm(`Hapus lampiran "${att.filename}"?`)) return;
    try {
      await api.delete(`/prospects/${prospect.id}/attachments/${att.id}`);
      toast.success("Lampiran dihapus");
      await refreshProspect();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Gagal hapus"); }
  };

  const copyText = async (text, label) => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); toast.success(`${label} disalin`); } catch { toast.error("Gagal salin"); }
  };

  const tabs = [
    { key: "info", label: "Informasi", icon: Info },
    { key: "pic", label: "PIC & Kontak", icon: Users },
    { key: "lampiran", label: "Lampiran", icon: FileStack, count: attachments.length + (prospect.quotations_count || 0) },
    { key: "riwayat", label: "Riwayat", icon: History, count: history.length },
  ];

  const canQuickAction = canEdit && !isWon && !isLost;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-5xl max-h-[92vh] overflow-y-auto rounded-3xl border-0 p-0"
        data-testid="prospect-detail-modal"
        style={{
          background: "linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(232,240,254,0.92) 100%)",
          boxShadow: "0 32px 80px -20px rgba(59,74,99,0.30)",
        }}
      >
        <DialogTitle className="sr-only">Detail Prospek {prospect.customer}</DialogTitle>
        <DialogDescription className="sr-only">Detail lengkap prospek</DialogDescription>

        <div className="p-6 space-y-5">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#6B8FE2] font-bold">
            Detail Prospek{mode === "edit" ? " · Mode Edit" : ""}
          </p>

          {/* HERO */}
          <div className="rounded-2xl p-5 backdrop-blur-xl" style={{ background: meta.heroBg, border: `1px solid ${meta.heroBorder}`, boxShadow: "0 10px 30px -12px rgba(20,22,31,0.12), inset 0 1px 0 rgba(255,255,255,0.55)" }}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${meta.chip}`}>
                    {isWon ? "✓ Deal" : isLost ? "✕ Lost" : `${meta.label} · Stage ${prospect.rate}`}
                  </span>
                  {prospect.client_category && (
                    <span className="inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#E6E2F4] text-[#4A3B77]">
                      {CATEGORY_LABEL[prospect.client_category] || prospect.client_category}
                    </span>
                  )}
                  {source && (
                    <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${source.tint}`}>
                      {source.label}
                    </span>
                  )}
                </div>
                <h2 className="text-2xl font-bold text-[#090808] leading-tight truncate">{prospect.customer}</h2>
                <p className="text-sm text-[#626061] mt-0.5 truncate">{prospect.service}</p>
              </div>
              {prospect.pipeline_month && (
                <span className="inline-flex items-center h-7 px-3 rounded-full text-[11px] font-bold uppercase tracking-widest bg-white/70 text-[#3B4A63] border border-white/80 shrink-0 shadow-sm font-num tabular-nums" data-testid="prospect-pipeline-month">
                  <span className="text-[9px] text-[#A0A3A8] mr-1.5">BULAN</span>
                  {prospect.pipeline_month}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
              <div className="flex items-center gap-2">
                <Avatar name={prospect.owner_name} picture={prospect.owner_picture} size="sm" className="!w-7 !h-7 !text-[11px]" />
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Sales</div>
                  <div className="text-sm font-semibold text-[#090808]">{prospect.owner_nickname || prospect.owner_name || "-"}</div>
                </div>
              </div>
              <div className="w-px h-8 bg-white/70 hidden sm:block" />
              <div>
                <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Last Follow-up</div>
                <div className="text-sm font-semibold text-[#090808] font-num tabular-nums">{prospect.last_follow_up ? fmtDate(prospect.last_follow_up) : "—"}</div>
              </div>
              <div className="w-px h-8 bg-white/70 hidden sm:block" />
              <div>
                <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Umur Prospek</div>
                <div className="text-sm font-semibold text-[#090808] font-num tabular-nums">{ageDays} hari</div>
              </div>
              <div className="flex-1" />
              {canQuickAction && (
                <>
                  <button onClick={onDeal} data-testid="prospect-detail-deal" className="h-9 rounded-full px-4 text-xs font-semibold text-white bg-gradient-to-r from-[#57D28A] to-[#3EB876] shadow-sm inline-flex items-center gap-1.5 hover:from-[#3EB876] hover:to-[#2FA365]">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Tandai Deal
                  </button>
                  <button onClick={onLost} data-testid="prospect-detail-lost" className="h-9 rounded-full px-4 text-xs font-semibold text-[#CE6968] bg-white/80 border border-[#FFB3BF] inline-flex items-center gap-1.5 hover:bg-white">
                    <XCircle className="w-3.5 h-3.5" /> Tandai Lost
                  </button>
                </>
              )}
              {(isWon || isLost) && canEdit && (
                <button onClick={onReopen} data-testid="prospect-detail-reopen" className="h-9 rounded-full px-4 text-xs font-semibold text-[#3B4A63] bg-white/80 border border-[#B0C7EA] inline-flex items-center gap-1.5 hover:bg-white">
                  <Undo2 className="w-3.5 h-3.5" /> Buka Kembali
                </button>
              )}
            </div>
          </div>

          {/* STAT STRIP */}
          <div className="grid grid-cols-3 rounded-2xl bg-white/70 border border-white/80 backdrop-blur-xl overflow-hidden" style={{ boxShadow: "0 6px 18px rgba(59,74,99,0.08)" }}>
            <div className="p-4 border-r border-[#DCE0E7]/60">
              <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Nilai Prospek</div>
              <div className="text-lg font-bold text-[#090808] font-num tabular-nums mt-1">{fmtIDR(prospect.prospect_value)}</div>
            </div>
            <div className="p-4 border-r border-[#DCE0E7]/60">
              <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Realisasi</div>
              <div className={`text-lg font-bold font-num tabular-nums mt-1 ${prospect.realization > 0 ? "text-[#57D28A]" : "text-[#A0A3A8]"}`}>{fmtIDR(prospect.realization)}</div>
              <div className="text-[9px] text-[#A0A3A8]">{isWon ? "Deal terealisasi" : "Belum deal"}</div>
            </div>
            <div className="p-4">
              <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Win Probability</div>
              <div className="text-lg font-bold text-[#B39548] font-num tabular-nums mt-1">
                {isWon ? "100%" : isLost ? "0%" : `${winProb}%`}
              </div>
              <div className="text-[9px] text-[#B39548]">
                {isWon ? "Deal" : isLost ? "Lost" : `Stage ${meta.label}`}
              </div>
            </div>
          </div>

          {/* TAB BAR */}
          <div className="flex items-center justify-between border-b border-[#DCE0E7] gap-3">
            <div className="flex items-center overflow-x-auto flex-1">
              {tabs.map((t) => {
                const Ico = t.icon;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key} onClick={() => setTab(t.key)} data-testid={`prospect-tab-${t.key}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-3 text-sm transition-colors whitespace-nowrap border-b-2 ${
                      active ? (mode === "edit" ? "text-[#3B4A63] border-[#3B4A63] font-bold" : "text-[#090808] border-[#090808] font-bold") : "text-[#A0A3A8] border-transparent hover:text-[#626061]"
                    }`}
                  >
                    <Ico className="w-3.5 h-3.5" />
                    {t.label}
                    {typeof t.count === "number" && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${active ? "bg-[#090808] text-white" : "bg-[#F4F5F7] text-[#626061]"}`}>{t.count}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 pb-2">
              {mode === "read" && canEdit && !isWon && !isLost && (
                <IconAction icon={Pencil} label="Edit" variant="info" size="sm" onClick={() => setMode("edit")} testid="prospect-edit-toggle" />
              )}
              {mode === "edit" && (
                <>
                  <IconAction icon={X} label="Batal" size="sm" onClick={cancelEdit} disabled={saving} testid="prospect-edit-cancel" />
                  <IconAction icon={Save} label={saving ? "Menyimpan…" : "Simpan Perubahan"} variant="primary" size="sm" onClick={saveDetail} disabled={saving} testid="prospect-save-btn" />
                </>
              )}
            </div>
          </div>

          {/* TAB CONTENT */}
          {tab === "info" && (
            mode === "read" ? (
              <div className="space-y-0 rounded-2xl bg-white/50 border border-[#E5EEFA]/60 px-5 pt-2 pb-4">
                <InfoRow label="Service" value={prospect.service || "-"} />
                <InfoRow label="Klien" value={`${prospect.customer}${prospect.client_category ? ` · ${CATEGORY_LABEL[prospect.client_category] || prospect.client_category}` : ""}`} />
                <InfoRow label="Stage" value={<span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${meta.chip}`}>{isWon ? "Deal" : isLost ? "Lost" : meta.label}</span>} />
                <InfoRow label="Sumber Prospek" value={source ? <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${source.tint}`}>{source.label}</span> : <span className="text-[#A0A3A8]">—</span>} />
                {prospect.lead_source_detail && <InfoRow label="Detail Sumber" value={prospect.lead_source_detail} />}
                <InfoRow label="Sales" value={prospect.owner_nickname || prospect.owner_name || "-"} />
                <InfoRow label="Nilai Prospek" value={fmtIDR(prospect.prospect_value)} valueClass="font-num" />
                <InfoRow label="Realisasi" value={<span className={prospect.realization > 0 ? "text-[#57D28A] font-num" : "text-[#A0A3A8] font-num"}>{fmtIDR(prospect.realization)}</span>} />
                <InfoRow label="Bulan Pipeline" value={prospect.pipeline_month || "-"} valueClass="font-num" />
                <InfoRow
                  label="Last Follow-up"
                  value={
                    <div className="inline-flex items-center gap-2">
                      <span className={prospect.last_follow_up ? "font-num" : "text-[#A0A3A8]"}>
                        {prospect.last_follow_up ? `${fmtDate(prospect.last_follow_up)} · ${computeAgeDays(prospect.last_follow_up)} hari lalu` : "Belum ada"}
                      </span>
                      {canEdit && !isWon && !isLost && (
                        <button
                          onClick={quickFollowUp}
                          disabled={saving}
                          data-testid="prospect-quick-followup"
                          title="Tandai follow-up hari ini"
                          className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full text-[10px] font-bold text-white bg-gradient-to-r from-[#6B8FE2] to-[#3B4A63] hover:from-[#5A7ED0] hover:to-[#2C3B54] shadow-sm transition"
                        >
                          <Phone className="w-3 h-3" /> Follow up
                        </button>
                      )}
                    </div>
                  }
                />
                <InfoRow label="Dibuat" value={prospect.created_at ? fmtDate(prospect.created_at.slice(0, 10)) : "-"} valueClass="font-num text-[#626061]" />
                <div className="pt-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mb-2">Remark / Catatan</div>
                  {prospect.remark ? (
                    <div className="rounded-xl bg-white/70 border border-[#DCE0E7]/70 p-3 text-sm text-[#3B4A63] whitespace-pre-wrap">{prospect.remark}</div>
                  ) : (
                    <div className="rounded-xl bg-[#F9FAFC] border border-[#DCE0E7]/70 p-3 text-sm text-[#A0A3A8] italic">Belum ada catatan.</div>
                  )}
                </div>
              </div>
            ) : (
              <EditInfoForm
                form={form}
                setForm={setForm}
                clients={clients}
                onClientsChanged={(newClient) => {
                  // append newly-created client so it shows up in picker
                  setClients((prev) => (prev.some((c) => c.id === newClient.id) ? prev : [...prev, newClient]));
                }}
              />
            )
          )}

          {tab === "pic" && <TabPIC prospect={prospect} onCopy={copyText} onChanged={async () => { await refreshProspect(); await refreshHistory(); onChanged?.(); }} />}

          {tab === "lampiran" && (
            <div className="space-y-5">
              <TabLampiran
                attachments={attachments} canEdit={canEdit} uploading={uploading}
                userMap={userMap}
                onUpload={uploadAttachments} onOpen={openAttachment} onDelete={deleteAttachment}
                inputRef={attachInputRef}
              />
              <QuotationAttachments
                prospectId={prospect.id}
                onOpenQuotation={(qid) => { onClose?.(); navigate(`/quotations?open=${qid}`); }}
              />
            </div>
          )}

          {tab === "riwayat" && (
            <TabRiwayat
              history={history}
              prospectId={prospect.id}
              users={users}
              currentUser={user}
              ownerId={prospect.owner_id}
              onNoteAdded={async () => { await refreshHistory(); onChanged?.(); }}
            />
          )}

          {/* FOOTER */}
          <div className="pt-4 border-t border-[#E5EEFA]/60 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {canDelete && (
                <IconAction icon={Trash2} label="Hapus Prospek" variant="danger" onClick={onDelete} testid="prospect-delete-btn" />
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => { onClose?.(); navigate(`/quotations?prospect=${prospect.id}`); }}
                data-testid="prospect-create-quotation"
                className="rounded-full h-10 bg-[#2E5BFF] hover:bg-[#254BDB] text-white gap-2"
              >
                <FileSignature className="w-4 h-4" /> Buat Penawaran
              </Button>
              <IconAction icon={X} label="Tutup" onClick={onClose} testid="prospect-modal-close" />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InfoRow({ label, value, valueClass = "" }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#F4F5F7] last:border-0 gap-4">
      <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold shrink-0">{label}</span>
      <span className={`text-sm text-[#090808] font-semibold text-right ${valueClass}`}>{value}</span>
    </div>
  );
}

function EditInfoForm({ form, setForm, clients, onClientsChanged }) {
  return (
    <div className="rounded-2xl border-2 border-[#B0C7EA] bg-[#F5F9FF]/50 p-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Klien</Label>
          <ClientChangeControl form={form} setForm={setForm} clients={clients} onClientsChanged={onClientsChanged} />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Service</Label>
          <Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="rounded-xl bg-white h-11 mt-1" data-testid="prospect-edit-service" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Stage</Label>
          <Select value={String(form.rate)} onValueChange={(v) => setForm({ ...form, rate: Number(v) })}>
            <SelectTrigger className="rounded-xl bg-white h-11 mt-1" data-testid="prospect-edit-stage"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#FF6B7D]" /> Hot</span>
              </SelectItem>
              <SelectItem value="2">
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#FFDB52]" /> Warm</span>
              </SelectItem>
              <SelectItem value="3">
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#97ADD4]" /> Cold</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Sumber Prospek</Label>
          <Select value={form.lead_source || ""} onValueChange={(v) => setForm({ ...form, lead_source: v })}>
            <SelectTrigger className="rounded-xl bg-white h-11 mt-1" data-testid="prospect-edit-source"><SelectValue placeholder="Pilih sumber" /></SelectTrigger>
            <SelectContent>
              {Object.entries(LEAD_SOURCE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Detail Sumber</Label>
          <Input value={form.lead_source_detail} onChange={(e) => setForm({ ...form, lead_source_detail: e.target.value })} className="rounded-xl bg-white h-11 mt-1" data-testid="prospect-edit-source-detail" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Nilai Prospek</Label>
          <NumberInput value={form.prospect_value} onChange={(v) => setForm({ ...form, prospect_value: v })} className="rounded-xl bg-white h-11 mt-1" data-testid="prospect-edit-value" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Bulan Pipeline (YYYY-MM)</Label>
          <Input value={form.pipeline_month || ""} onChange={(e) => setForm({ ...form, pipeline_month: e.target.value })} className="rounded-xl bg-white h-11 mt-1 font-num" placeholder="2026-08" data-testid="prospect-edit-month" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Last Follow-up</Label>
          <DateInput
            value={form.last_follow_up || ""}
            onChange={(e) => setForm({ ...form, last_follow_up: e.target.value })}
            className="rounded-xl bg-white h-11"
            wrapperClassName="mt-1"
            data-testid="prospect-edit-followup"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Remark / Catatan</Label>
          <Textarea value={form.remark || ""} onChange={(e) => setForm({ ...form, remark: e.target.value })} rows={3} className="rounded-xl bg-white mt-1" data-testid="prospect-edit-remark" />
        </div>
      </div>
    </div>
  );
}

function TabPIC({ prospect, onCopy, onChanged }) {
  const [clientPics, setClientPics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(null);

  useEffect(() => {
    if (!prospect?.client_id) return;
    setLoading(true);
    api.get(`/clients/${prospect.client_id}`).then((r) => {
      setClientPics((r.data.pics || []).filter((p) => p.name || p.phone || p.email));
    }).catch(() => setClientPics([])).finally(() => setLoading(false));
  }, [prospect?.client_id]);

  const currentKey = `${(prospect.pic_name || "").trim()}|${(prospect.pic_phone || "").trim()}|${(prospect.pic_email || "").trim()}`;

  const applyPic = async (p) => {
    setApplying(p.id || p.name);
    try {
      await api.patch(`/prospects/${prospect.id}`, {
        customer: prospect.customer, service: prospect.service, rate: prospect.rate,
        prospect_value: prospect.prospect_value || 0,
        realization: prospect.realization || 0,
        pic_id: p.id || "",
        pic_name: p.name || "", pic_position: p.position || "",
        pic_phone: p.phone || "", pic_email: p.email || "",
        last_follow_up: prospect.last_follow_up || null,
        remark: prospect.remark || "",
        pipeline_month: prospect.pipeline_month || null,
        lead_source: prospect.lead_source || null,
        lead_source_detail: prospect.lead_source_detail || "",
      });
      toast.success(`PIC diubah ke ${p.name}`);
      onChanged?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Gagal ubah PIC");
    } finally { setApplying(null); }
  };

  const renderPicCard = (p, isActive, isFromClient) => {
    const waLink = p.phone ? `https://wa.me/${cleanPhone(p.phone)}?text=${encodeURIComponent(`Halo${p.name ? " " + p.name : ""}, saya dari tim IPTec terkait ${prospect.customer}.`)}` : "";
    return (
      <div key={p.id || `${p.name}-${p.phone}`} className={`rounded-2xl p-4 transition ${isActive ? "bg-gradient-to-br from-[#FFF8E0] to-[#FFFDF3] border-2 border-[#F5D66B]" : "bg-white border border-[#DCE0E7]"}`}>
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {isActive && <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#FFDB52] text-[#7A5B00]">⭐ PIC Aktif</span>}
            {p.is_default && isFromClient && !isActive && <span className="inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#F4F5F7] text-[#626061]">Utama Klien</span>}
          </div>
          {isFromClient && !isActive && (
            <button onClick={() => applyPic(p)} disabled={!!applying} data-testid={`prospect-set-pic-${p.id || p.name}`} className="inline-flex items-center gap-1 h-7 px-3 rounded-full text-[11px] font-semibold text-white bg-[#3B4A63] hover:bg-[#2C3B54] shadow-sm disabled:opacity-60">
              {applying === (p.id || p.name) ? "..." : "Set PIC"}
            </button>
          )}
        </div>
        <div className="text-base font-bold text-[#090808]">{p.name || "-"}</div>
        <div className="text-xs text-[#626061] mb-3">{p.position || "-"}</div>
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center gap-2 text-[#090808] font-num tabular-nums">
            <Phone className="w-3.5 h-3.5 text-[#3B4A63] shrink-0" />
            <span className="truncate">{p.phone || <span className="text-[#A0A3A8]">Belum diisi</span>}</span>
            {p.phone && (
              <div className="ml-auto flex items-center gap-1.5 shrink-0">
                <a href={waLink} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-[#25D366] hover:bg-[#1EB855] text-white flex items-center justify-center" title="WhatsApp"><MessageCircle className="w-3.5 h-3.5" /></a>
                <button onClick={() => onCopy(p.phone, "No HP")} className="w-8 h-8 rounded-full bg-white border border-[#DCE0E7] text-[#3B4A63] hover:bg-[#E5EEFA] flex items-center justify-center" title="Salin"><Copy className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 text-[#090808]">
            <Mail className="w-3.5 h-3.5 text-[#3B4A63] shrink-0" />
            <span className="truncate">{p.email || <span className="text-[#A0A3A8]">Belum diisi</span>}</span>
            {p.email && (
              <div className="ml-auto flex items-center gap-1.5 shrink-0">
                <a href={`mailto:${p.email}`} className="w-8 h-8 rounded-full bg-[#3B4A63] hover:bg-[#2C3B54] text-white flex items-center justify-center" title="Email"><Mail className="w-3.5 h-3.5" /></a>
                <button onClick={() => onCopy(p.email, "Email")} className="w-8 h-8 rounded-full bg-white border border-[#DCE0E7] text-[#3B4A63] hover:bg-[#E5EEFA] flex items-center justify-center" title="Salin"><Copy className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const activePic = (prospect.pic_name || prospect.pic_phone || prospect.pic_email)
    ? { name: prospect.pic_name || "", position: prospect.pic_position || "", phone: prospect.pic_phone || "", email: prospect.pic_email || "" }
    : null;
  const otherClientPics = clientPics.filter((p) => {
    const k = `${(p.name || "").trim()}|${(p.phone || "").trim()}|${(p.email || "").trim()}`;
    return k !== currentKey;
  });

  return (
    <div className="space-y-4">
      {activePic && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mb-2">PIC Aktif untuk Prospek Ini</div>
          {renderPicCard(activePic, true, false)}
        </div>
      )}
      {prospect.client_id && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold">
              {activePic ? "Ganti PIC dari Data Klien" : "Pilih PIC dari Data Klien"}
            </div>
            {loading && <span className="text-[10px] text-[#A0A3A8]">Memuat…</span>}
          </div>
          {otherClientPics.length === 0 && !loading ? (
            <div className="rounded-2xl bg-[#F9FAFC] border border-[#DCE0E7] p-4 text-center text-xs text-[#A0A3A8]">
              {activePic ? "Tidak ada PIC lain di klien ini." : "Klien belum punya PIC. Tambah di halaman Klien."}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {otherClientPics.map((p) => renderPicCard(p, false, true))}
            </div>
          )}
        </div>
      )}
      {!activePic && !prospect.client_id && (
        <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]">
          Belum ada PIC untuk prospek ini.
        </div>
      )}
      <div className="text-[11px] text-[#A0A3A8]">
        💡 Untuk menambah PIC baru (Direktur, Finance, dsb.), edit klien di halaman Klien. Semua PIC klien akan bisa dipakai di prospek berikutnya.
      </div>
    </div>
  );
}

function TabLampiran({ attachments, canEdit, uploading, userMap, onUpload, onOpen, onDelete, inputRef }) {
  const iconForFile = (name) => {
    const ext = (name || "").split(".").pop()?.toLowerCase();
    if (["pdf"].includes(ext)) return { label: "PDF", bg: "bg-[#FDE7E9]", fg: "text-[#CE6968]" };
    if (["xls", "xlsx", "csv"].includes(ext)) return { label: "XLS", bg: "bg-[#E4F9EC]", fg: "text-[#3EB876]" };
    if (["doc", "docx"].includes(ext)) return { label: "DOC", bg: "bg-[#DDEBFB]", fg: "text-[#2F4B70]" };
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return { label: "IMG", bg: "bg-[#E6E2F4]", fg: "text-[#4A3B77]" };
    return { label: "FILE", bg: "bg-[#F4F5F7]", fg: "text-[#626061]" };
  };
  return (
    <div className="space-y-4">
      {canEdit && (
        <>
          <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt" className="hidden" onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }} data-testid="prospect-att-input" />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="prospect-att-btn" className="w-full rounded-2xl border-2 border-dashed border-[#B0C7EA] bg-[#F5F9FF]/50 hover:bg-[#EAF2FD]/70 transition py-6 flex flex-col items-center gap-1">
            <Paperclip className="w-5 h-5 text-[#3B4A63]" />
            <div className="text-sm font-semibold text-[#3B4A63]">{uploading ? "Mengupload…" : "Klik untuk upload lampiran"}</div>
            <div className="text-[10px] text-[#A0A3A8]">Quotation · Proposal · Kontrak · max 15MB per file</div>
          </button>
          <div className="text-[11px] text-[#A0A3A8]">
            💡 Setelah prospek deal, lampiran ini otomatis muncul di modal Proyek.
          </div>
        </>
      )}
      {attachments.length === 0 ? (
        <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]">Belum ada lampiran untuk prospek ini.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {attachments.map((a) => {
            const ic = iconForFile(a.filename);
            const uploader = a.uploaded_by ? (userMap || {})[a.uploaded_by] : null;
            const uploaderName = (uploader?.nickname || uploader?.name) || a.uploaded_by_name || "-";
            return (
              <div key={a.id} onClick={() => onOpen(a.id)} className="rounded-2xl bg-white border border-[#DCE0E7]/60 p-4 flex items-center gap-3 hover:shadow-md transition cursor-pointer" data-testid={`prospect-att-${a.id}`}>
                <div className={`w-11 h-11 rounded-xl ${ic.bg} flex items-center justify-center ${ic.fg} font-bold text-[10px] shrink-0`}>{ic.label}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#090808] truncate">{a.filename}</div>
                  <div className="text-[11px] text-[#A0A3A8] mt-0.5 flex items-center gap-1.5 min-w-0">
                    <Avatar name={uploader?.name || a.uploaded_by_name} picture={uploader?.picture} size="sm" className="!w-4 !h-4 !text-[8px] !border-0 shrink-0" />
                    <span className="truncate">{uploaderName}{a.size ? ` · ${Math.round(a.size / 1024)} KB` : ""}</span>
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); onOpen(a.id); }} className="w-8 h-8 rounded-full hover:bg-[#F4F5F7] text-[#3B4A63] flex items-center justify-center shrink-0" title="Buka"><Download className="w-3.5 h-3.5" /></button>
                {canEdit && (
                  <button onClick={(e) => onDelete(a, e)} className="w-8 h-8 rounded-full hover:bg-[#FDE7E9] text-[#CE6968] flex items-center justify-center shrink-0" title="Hapus" data-testid={`prospect-att-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TabRiwayat({ history, prospectId, users, currentUser, ownerId, onNoteAdded }) {
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const canPost = !!prospectId;
  const recipientCandidates = (users || []).filter((u) =>
    u.is_active !== false && u.role !== "finance" && u.user_id !== currentUser?.user_id && u.user_id !== ownerId
  );

  const toggleRecipient = (uid) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(uid)) n.delete(uid); else n.add(uid);
      return n;
    });
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/prospects/${prospectId}/notes`, {
        note,
        recipient_ids: Array.from(selected),
      });
      toast.success(selected.size > 0 ? `Catatan dikirim ke ${selected.size} orang` : "Catatan ditambahkan");
      setNote("");
      setSelected(new Set());
      onNoteAdded?.();
    } catch {
      toast.error("Gagal menyimpan catatan");
    } finally {
      setBusy(false);
    }
  };

  const userMap = {};
  (users || []).forEach((u) => { userMap[u.user_id] = u; });

  return (
    <div className="space-y-5" data-testid="prospect-riwayat-tab">
      {/* Composer */}
      {canPost && (
        <div className="rounded-2xl bg-white/70 border border-[#DCE0E7]/60 p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold">Tambah Catatan</div>
          <Textarea
            data-testid="prospect-note-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Tulis update follow-up, hasil meeting, atau info penting…"
            rows={3}
            className="rounded-xl bg-white resize-none"
          />
          {recipientCandidates.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold mb-1.5">
                Kirim ke <span className="text-[#A0A3A8]">(opsional)</span>
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="prospect-recipient-chips">
                {recipientCandidates.map((u) => {
                  const active = selected.has(u.user_id);
                  return (
                    <button
                      key={u.user_id}
                      type="button"
                      onClick={() => toggleRecipient(u.user_id)}
                      data-testid={`prospect-recipient-chip-${u.user_id}`}
                      className={`inline-flex items-center gap-1.5 pl-1 pr-2.5 py-0.5 rounded-full border text-xs transition-colors ${
                        active ? "bg-[#090808] border-[#090808] text-white" : "bg-white border-[#DCE0E7] text-[#626061] hover:border-[#090808]/40"
                      }`}
                    >
                      <Avatar name={u.name} picture={u.picture} size="xs" />
                      <span>{u.nickname || u.name}</span>
                      {active && <Check className="w-3 h-3" strokeWidth={3} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={addNote}
              disabled={busy || !note.trim()}
              data-testid="prospect-add-note-btn"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-semibold text-white bg-[#090808] hover:bg-black disabled:opacity-60 shadow-[0_6px_16px_rgba(9,8,8,0.2)]"
            >
              {selected.size > 0 ? <Send className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              {busy ? "Menyimpan…" : selected.size > 0 ? `Kirim (${selected.size})` : "Tambah Catatan"}
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      {(!history || history.length === 0) ? (
        <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]">Belum ada riwayat aktivitas.</div>
      ) : (
        <ol className="relative border-l-2 border-[#DCE0E7] ml-3 space-y-5" data-testid="prospect-history-list">
          {history.map((h) => {
            const dot = h.action === "won" || h.action === "deal" ? "bg-[#57D28A]" : h.action === "lost" ? "bg-[#A0A3A8]" : h.action === "attachment" ? "bg-[#4A3B77]" : h.action === "note" ? "bg-[#B39548]" : "bg-[#6B8FE2]";
            const actor = h.actor_id ? userMap[h.actor_id] : null;
            const actorPicture = actor?.picture || null;
            const actorDisplay = actor?.nickname || actor?.name || h.actor_name || "System";
            const recipientObjs = (h.recipient_ids || []).map((rid) => userMap[rid]).filter(Boolean);
            return (
              <li key={h.id} className="pl-6 relative" data-testid={`prospect-history-${h.id}`}>
                <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full ${dot} border-4 border-white shadow-sm`} />
                <div className="text-[10px] text-[#A0A3A8] font-bold uppercase tracking-widest font-num tabular-nums">
                  {h.created_at ? new Date(h.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
                </div>
                <div className="text-sm font-semibold text-[#090808] mt-0.5 whitespace-pre-wrap">{h.note || h.action || "-"}</div>
                <div className="text-xs text-[#626061] mt-1 flex items-center gap-1.5 flex-wrap">
                  <Avatar name={actor?.name || h.actor_name} picture={actorPicture} size="sm" className="!w-5 !h-5 !text-[9px] !border-0 shrink-0" />
                  <span>{actorDisplay}</span>
                  {recipientObjs.length > 0 && (
                    <>
                      <span className="text-[#A0A3A8]">→</span>
                      {recipientObjs.map((u) => (
                        <span key={u.user_id} className="inline-flex items-center gap-1 text-[#3B4A63]">
                          <Avatar name={u.name} picture={u.picture} size="sm" className="!w-5 !h-5 !text-[9px] !border-0 shrink-0" />
                          <span>{u.nickname || u.name}</span>
                        </span>
                      ))}
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}


/* ---------- Client Change Control (Read-only chip + picker) ---------- */

const CAT_LABEL_META = {
  pemerintah: { label: "Pemerintah", tint: "bg-[#FDE7E9] text-[#CE6968]" },
  bumn: { label: "BUMN", tint: "bg-[#E5EEFA] text-[#3B4A63]" },
  swasta: { label: "Swasta", tint: "bg-[#C9F0D6] text-[#4A6D53]" },
  partner: { label: "Partner", tint: "bg-[#E6E2F4] text-[#4A3B77]" },
};

function ClientChangeControl({ form, setForm, clients, onClientsChanged }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("select"); // "select" | "new"
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState("swasta");
  const [creating, setCreating] = useState(false);
  const cat = CAT_LABEL_META[form.client_category] || null;

  const applyExisting = (c) => {
    setForm({
      ...form,
      client_id: c.id,
      customer: c.name,
      client_category: c.category || "",
    });
    setOpen(false);
  };

  const createNew = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Nama klien wajib diisi"); return; }
    setCreating(true);
    try {
      const r = await api.post("/clients", {
        name, category: newCategory, pics: [], address: "", website: "", notes: "", logo: "",
      });
      const created = r.data;
      onClientsChanged?.(created);
      setForm({
        ...form,
        client_id: created.id,
        customer: created.name,
        client_category: created.category || newCategory,
      });
      toast.success(`Klien "${created.name}" dibuat`);
      setNewName("");
      setNewCategory("swasta");
      setMode("select");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal buat klien");
    } finally { setCreating(false); }
  };

  return (
    <div className="mt-1 flex items-center gap-2">
      <div className="flex-1 min-w-0 rounded-xl bg-white h-11 border border-[#DCE0E7] px-3 flex items-center gap-2" data-testid="prospect-edit-client-chip">
        <Building2 className="w-4 h-4 text-[#3B4A63] shrink-0" />
        <span className="text-sm font-semibold text-[#090808] truncate">{form.customer || "-"}</span>
        {cat && (
          <span className={`inline-flex items-center h-5 px-2 rounded-full text-[9px] font-bold uppercase tracking-widest shrink-0 ${cat.tint}`}>
            {cat.label}
          </span>
        )}
      </div>
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setMode("select"); }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="prospect-edit-change-client"
            className="inline-flex items-center gap-1.5 h-11 px-3 rounded-xl text-xs font-semibold text-[#3B4A63] bg-white border border-[#DCE0E7] hover:bg-[#E5EEFA] shrink-0"
            title="Ganti klien"
          >
            <Repeat className="w-3.5 h-3.5" /> Ganti klien
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[380px] p-0" onWheel={(e) => e.stopPropagation()}>
          <div className="p-2 border-b border-[#F4F5F7] flex items-center gap-1">
            <button
              type="button"
              onClick={() => setMode("select")}
              data-testid="prospect-change-client-tab-select"
              className={`flex-1 h-8 rounded-lg text-xs font-semibold transition-colors ${mode === "select" ? "bg-[#090808] text-white" : "text-[#626061] hover:bg-[#F5F3FF]"}`}
            >
              Pilih dari database
            </button>
            <button
              type="button"
              onClick={() => setMode("new")}
              data-testid="prospect-change-client-tab-new"
              className={`flex-1 h-8 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 transition-colors ${mode === "new" ? "bg-[#090808] text-white" : "text-[#626061] hover:bg-[#F5F3FF]"}`}
            >
              <Plus className="w-3 h-3" /> Klien baru
            </button>
          </div>
          {mode === "select" ? (
            <Command>
              <CommandInput placeholder="Cari klien..." data-testid="prospect-change-client-search" />
              <CommandList className="max-h-64">
                <CommandEmpty>
                  <div className="text-center py-3 text-xs text-[#A0A3A8]">Klien tidak ditemukan</div>
                </CommandEmpty>
                <CommandGroup>
                  {(clients || []).map((c) => (
                    <CommandItem
                      key={c.id}
                      value={`${c.name} ${c.category || ""}`}
                      onSelect={() => applyExisting(c)}
                      data-testid={`prospect-change-client-option-${c.id}`}
                      className="gap-2"
                    >
                      {c.logo ? (
                        <img src={c.logo} alt="" className="w-6 h-6 rounded-md object-cover" />
                      ) : (
                        <div className="w-6 h-6 rounded-md bg-[#DCE0E7] text-[#090808] flex items-center justify-center">
                          <Building2 className="w-3 h-3" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{c.name}</div>
                        <div className="text-[10px] text-[#A0A3A8] uppercase tracking-widest">
                          {(CAT_LABEL_META[c.category] || {}).label || c.category}
                        </div>
                      </div>
                      {form.client_id === c.id && <Check className="w-4 h-4 text-[#090808]" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          ) : (
            <div className="p-3 space-y-2.5">
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Nama Klien</Label>
                <Input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Nama perusahaan / lembaga"
                  className="rounded-xl h-10 mt-1"
                  data-testid="prospect-change-client-new-name"
                />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Kategori</Label>
                <Select value={newCategory} onValueChange={setNewCategory}>
                  <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="prospect-change-client-new-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CAT_LABEL_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button type="button" onClick={() => setMode("select")} className="h-8 px-3 rounded-lg text-xs font-semibold text-[#626061] hover:bg-[#F4F5F7]">Batal</button>
                <button
                  type="button"
                  onClick={createNew}
                  disabled={creating || !newName.trim()}
                  data-testid="prospect-change-client-new-save"
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold text-white bg-[#090808] hover:bg-black disabled:opacity-60"
                >
                  <Plus className="w-3 h-3" /> {creating ? "Membuat…" : "Buat & pilih"}
                </button>
              </div>
              <p className="text-[10px] text-[#A0A3A8]">Klien baru langsung tersimpan di halaman Klien.</p>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
